import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Chip,
  Stack,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Class as ClassIcon,
  Home as HomeIcon,
  AccessTime as AccessTimeIcon,
  LocationOn as LocationOnIcon,
  People as PeopleIcon,
  AttachMoney as AttachMoneyIcon,
  FitnessCenter as FitnessCenterIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  CalendarToday as CalendarTodayIcon,
  ManageAccounts as ManageAccountsIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import { classAPI, classInstanceAPI, memberProfileAPI } from '../../services/api';
import { Class, ClassBooking, ClassInstance, MemberProfile } from '../../types';
import { formatDate, formatTimeString } from '../../utils/dateUtils';
import { useAuth } from '../../context/AuthContext';

const ViewClassPage: React.FC = () => {
  const { classId } = useParams<{ classId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  const [classData, setClassData] = useState<Class | null>(null);
  const [classInstances, setClassInstances] = useState<ClassInstance[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [instancesLoading, setInstancesLoading] = useState<boolean>(false);
  const [registerMemberDialog, setRegisterMemberDialog] = useState<boolean>(false);
  const [members, setMembers] = useState<MemberProfile[]>([]);
  const [selectedMember, setSelectedMember] = useState<MemberProfile | null>(null);
  const [membersLoading, setMembersLoading] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [sessionStatusFilter, setSessionStatusFilter] = useState<string>('scheduled');

  // Determine referrer for breadcrumbs - check location state or fallback to calendar
  const referrer = (location.state as any)?.from || 'calendar';
  
  const getBreadcrumbPath = () => {
    switch (referrer) {
      case 'class-management':
        return { label: 'Class Management', icon: <FitnessCenterIcon />, path: '/class-management' };
      case 'dashboard':
        return { label: 'Dashboard', icon: <HomeIcon />, path: '/dashboard' };
      case 'calendar':
      default:
        return { label: 'Calendar', icon: <CalendarTodayIcon />, path: '/calendar' };
    }
  };
  
  const breadcrumbPath = getBreadcrumbPath();

  useEffect(() => {
    if (classId) {
      fetchClassData();
    }
  }, [classId]);

  const fetchClassData = async () => {
    if (!classId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await classAPI.getClassById(classId);
      setClassData(response.data);
      
      // Fetch class instances if user has appropriate permissions
      if (user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'coach') {
        fetchClassInstances();
      }
    } catch (error: any) {
      console.error('Error fetching class:', error);
      setError(error.response?.data?.message || 'Failed to fetch class details');
    } finally {
      setLoading(false);
    }
  };

  const fetchClassInstances = async () => {
    if (!classId) return;

    setInstancesLoading(true);
    try {
      const response = await classInstanceAPI.getInstances({
        parentClassId: classId,
        includeBookings: true
      });
      setClassInstances(response.data || []);
    } catch (error: any) {
      console.error('Error fetching class instances:', error);
      // Don't set main error state for instances fetch failures
    } finally {
      setInstancesLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'scheduled':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'draft':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getBookingStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'waitlist':
        return 'warning';
      case 'attended':
        return 'info';
      case 'no_show':
        return 'error';
      default:
        return 'default';
    }
  };

  const canEditClass = () => {
    return user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'coach';
  };

  const handleEditClass = () => {
    // Navigate to dedicated edit page
    navigate(`/classes/edit/${classId}`);
  };

  const handleRegisterMember = async () => {
    setRegisterMemberDialog(true);
    await fetchMembers();
  };

  const fetchMembers = async () => {
    setMembersLoading(true);
    try {
      const response = await memberProfileAPI.getAllMemberProfiles({
        status: 'active'
      });
      setMembers(response.data || []);
    } catch (error: any) {
      console.error('Error fetching members:', error);
      setError('Failed to fetch members');
    } finally {
      setMembersLoading(false);
    }
  };

  const confirmRegisterMember = async () => {
    if (!selectedMember || !classId) return;

    try {
      await classAPI.registerMemberToClass(classId, {
        memberProfileId: selectedMember.id,
        notes: ''
      });
      setSuccessMessage(`Successfully registered ${selectedMember.firstName} ${selectedMember.lastName} to the class`);
      setRegisterMemberDialog(false);
      setSelectedMember(null);
      // Refresh class data to update enrollment count
      fetchClassData();
    } catch (error: any) {
      console.error('Error registering member:', error);
      const errorMessage = error.response?.data?.message || 'Failed to register member to class';
      setError(errorMessage);
    }
  };

  const handleCloseRegisterDialog = () => {
    setRegisterMemberDialog(false);
    setSelectedMember(null);
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">{error}</Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mt: 2 }}
          >
            Go Back
          </Button>
        </Box>
      </Container>
    );
  }

  if (!classData) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <Alert severity="warning">Class not found</Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mt: 2 }}
          >
            Go Back
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Button
            component={RouterLink}
            to="/dashboard"
            color="inherit"
            startIcon={<HomeIcon />}
            sx={{ textTransform: 'none' }}
          >
            Dashboard
          </Button>
          {referrer !== 'dashboard' && (
            <Button
              component={RouterLink}
              to={breadcrumbPath.path}
              color="inherit"
              startIcon={breadcrumbPath.icon}
              sx={{ textTransform: 'none' }}
            >
              {breadcrumbPath.label}
            </Button>
          )}
          <Typography color="text.primary">Class Details</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
              <ClassIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
              {classData.name}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {classData.description}
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              variant="outlined"
            >
              Back
            </Button>
            {canEditClass() && (
              <Button
                startIcon={<EditIcon />}
                onClick={handleEditClass}
                variant="contained"
              >
                Edit Class
              </Button>
            )}
          </Stack>
        </Box>

        <Grid container spacing={3}>
          {/* Main Class Information */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Class Information
              </Typography>
                 {classData.recurringPattern && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      <ScheduleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Recurring Class
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {classData.recurringPattern.frequency.charAt(0).toUpperCase() + classData.recurringPattern.frequency.slice(1)}
                      {classData.recurringPattern.interval > 1 && ` (every ${classData.recurringPattern.interval} ${classData.recurringPattern.frequency}s)`}
                      {classData.recurringPattern.daysOfWeek && classData.recurringPattern.daysOfWeek.length > 0 && (
                        <span>
                          {' - '}
                          {classData.recurringPattern.daysOfWeek.map(day => 
                            ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][day]
                          ).join(', ')}
                        </span>
                      )}
                      {classData.recurringPattern.endDate && (
                        <span> until {formatDate(classData.recurringPattern.endDate)}</span>
                      )}
                    </Typography>
                  </Box>
                </>
              )}
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <CalendarTodayIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Date"
                        secondary={formatDate(classData.scheduleDate)}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <AccessTimeIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Time"
                        secondary={`${formatTimeString(classData.startTime)} - ${formatTimeString(classData.endTime)}`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <LocationOnIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Location"
                        secondary={classData.venue || 'Not specified'}
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <PersonIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Instructor"
                        secondary={classData.instructorName || classData.instructor || 'Not assigned'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <FitnessCenterIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Type & Level"
                        secondary={`${classData.type} - ${classData.level}`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <PeopleIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Capacity"
                        secondary={`${classData.capacity}`}
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
              
              {classData.oneTimeCost && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AttachMoneyIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body1">
                      <strong>Cost:</strong> ${classData.oneTimeCost}
                    </Typography>
                  </Box>
                </>
              )}

           
            </Paper>

            {/* Class Instances Section (for admin/coach users) */}
            {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'coach') && (
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Class Instances ({sessionStatusFilter === 'all' ? classInstances.length : classInstances.filter(i => i.status?.toLowerCase() === sessionStatusFilter).length})
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="session-status-filter-label">Status</InputLabel>
                    <Select
                      labelId="session-status-filter-label"
                      value={sessionStatusFilter}
                      label="Status"
                      onChange={(e) => setSessionStatusFilter(e.target.value)}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="scheduled">Scheduled</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                {instancesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : classInstances.filter(i => sessionStatusFilter === 'all' || i.status?.toLowerCase() === sessionStatusFilter).length > 0 ? (
                  <Grid container spacing={2}>
                    {classInstances.filter(i => sessionStatusFilter === 'all' || i.status?.toLowerCase() === sessionStatusFilter).map((instance) => (
                      <Grid size={{ xs: 12, md: 6 }} key={instance.id}>
                        <Card variant="outlined">
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                              <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                  {formatDate(instance.sessionDate)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {formatTimeString(instance.startTime)} - {formatTimeString(instance.endTime)}
                                </Typography>
                              </Box>
                              <Chip
                                label={instance.status}
                                color={getStatusColor(instance.status)}
                                size="small"
                              />
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <LocationOnIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                {instance.venue}
                              </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <PersonIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                {instance.instructor}
                              </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <PeopleIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                {instance.enrolledCount || 0} / {instance.capacity} enrolled
                              </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => navigate(`/class-instances/${instance.id}`, { state: { from: referrer } })}
                              >
                                View Details
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<ManageAccountsIcon />}
                                onClick={() => navigate(`/class-instances/${instance.id}`, { state: { from: referrer } })}
                              >
                                Manage Bookings
                              </Button>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {classInstances.length === 0
                      ? (classData?.recurringPattern ? 'No instances generated yet.' : 'This class has no scheduled instances.')
                      : `No ${sessionStatusFilter === 'all' ? '' : sessionStatusFilter + ' '}instances found.`}
                  </Typography>
                )}
              </Paper>
            )}
          </Grid>

          {/* Status Sidebar */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Status
                </Typography>
                <Chip
                  label={classData.active ? 'Active' : 'Inactive'}
                  color={getStatusColor(classData.active ? 'active' : 'inactive')}
                  sx={{ mb: 2 }}
                />
                <Typography variant="body2" color="text.secondary">
                  Created: {new Date(classData.createdAt).toLocaleDateString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Updated: {new Date(classData.updatedAt).toLocaleDateString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Register Member Dialog */}
        <Dialog
          open={registerMemberDialog}
          onClose={handleCloseRegisterDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Register Member to Class
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Autocomplete
                options={members}
                getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
                value={selectedMember}
                onChange={(event, newValue) => setSelectedMember(newValue)}
                loading={membersLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Member"
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {membersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Avatar sx={{ mr: 2 }}>
                      {option.firstName?.[0]}{option.lastName?.[0]}
                    </Avatar>
                    <Box>
                      <Typography variant="body1">
                        {option.firstName} {option.lastName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.email}
                      </Typography>
                    </Box>
                  </Box>
                )}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseRegisterDialog}>Cancel</Button>
            <Button
              onClick={confirmRegisterMember}
              variant="contained"
              disabled={!selectedMember}
            >
              Register Member
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success Message */}
        <Snackbar
          open={!!successMessage}
          autoHideDuration={6000}
          onClose={() => setSuccessMessage('')}
          message={successMessage}
        />
      </Box>
    </Container>
  );
};

export default ViewClassPage;